import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import client from '../api/client';
import SettingsLayout from './SettingsLayout';

function SettingsProfilePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { user, setUser } = useAuthStore();

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        address: user.address || '',
        addressLine2: '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
      });
    }
  }, [user]);

  // Prefill address from the user's first (oldest) insurance request
  useEffect(() => {
    const prefillFromOldestRequest = async () => {
      try {
        const { data } = await client.get('/insurance/requests');
        const requests = Array.isArray(data?.requests) ? data.requests : [];
        if (requests.length === 0) return;

        // API returns requests ordered DESC by created_at → last item is the oldest (first entered)
        const oldest = requests[requests.length - 1];

        setFormData((prev) => ({
          ...prev,
          address: oldest?.streetAddress || prev.address,
          addressLine2: oldest?.addressLine2 || prev.addressLine2,
          city: oldest?.city || prev.city,
          state: oldest?.state || prev.state,
          zipCode: oldest?.zipCode || prev.zipCode,
        }));
      } catch {
        // Silently ignore; user may not have requests yet
      }
    };

    prefillFromOldestRequest();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await client.put('/users/profile', formData);
      setUser(response.data.user);
      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsLayout>
      <div className="card">
        <div className="card-header">Profile</div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic Info Section */}
          <h4 style={{ marginTop: '0', marginBottom: '1rem', color: 'var(--color-green)', fontSize: '1rem' }}>
            Basic Information
          </h4>

          <div className="settings-form-grid">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email (read-only)</label>
            <input type="email" value={user?.email || ''} disabled style={{ backgroundColor: '#F5F5F5' }} />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Address Section */}
          <h4 style={{ marginTop: '2rem', marginBottom: '1rem', color: 'var(--color-green)', fontSize: '1rem' }}>
            Address
          </h4>

          <div className="form-group">
            <label htmlFor="address">Street Address</label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main St"
            />
          </div>

          <div className="form-group">
            <label htmlFor="addressLine2">Address Line 2</label>
            <input
              id="addressLine2"
              type="text"
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
              placeholder="Apartment, Suite, etc. (Optional)"
            />
          </div>

          <div className="settings-form-grid">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="New York"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                id="state"
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="NY"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="zipCode">ZIP Code</label>
            <input
              id="zipCode"
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="10001"
            />
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1.5rem' }} disabled={loading}>
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </SettingsLayout>
  );
}

export default SettingsProfilePage;
